import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { expand, map, Observable, reduce, takeWhile } from 'rxjs';
import { environment } from 'src/environments/environment';
import { EXPENSES_EXPORT_MAX_PAGES } from '../constants/expenses.constants';
import {
  keepsFreeText,
  takesReturnReason,
} from '../enums/expense-status.enum';
import {
  ExpenseDto,
  ExpenseDriverOptionsResponseDto,
  ExpenseStatusChangeRequestDto,
  ExpensesPaginatedResponseDto,
} from '../models/expense-api.model';
import {
  CreateExpensePayload,
  Expense,
  ExpenseDriverOption,
  ExpenseRef,
  ExpenseStatusChangePayload,
  ExpensesPage,
  ExpensesQuery,
  UpdateExpensePayload,
} from '../models/expense.model';
import { ExpensesDataSource } from './expenses.data-source';
import {
  centsToApiAmount,
  toDriverOption,
  toExpense,
  toExpensesPage,
  toPaginatedRequest,
} from './expenses.mapper';

/** Tamaño de página al recorrer todo el listado para exportar (máximo de la API). */
const EXPORT_PAGE_SIZE = 200;

/**
 * Implementación contra la Tracking API.
 *
 * Dos detalles del contrato que condicionan el código:
 *
 * - Los cuerpos JSON se construyen en PascalCase en el mapper. Las claves ya no
 *   distinguen mayúsculas, pero se envía la forma documentada.
 * - El alta y la edición son `multipart/form-data` con claves en camelCase. No
 *   se fija `Content-Type` a mano: el navegador debe añadir el `boundary`.
 *
 * El `Authorization` lo pone el `AuthInterceptor` global. Los cuatro endpoints
 * que escriben —alta, edición, cambio de estado y borrado— lo exigen y responden
 * `401` sin él: de ahí sale el usuario que queda registrado como revisor.
 */
@Injectable()
export class ExpensesHttpDataSource extends ExpensesDataSource {
  private readonly baseUrl = `${environment.trackingUrl}/operations/expenses`;

  constructor(private readonly http: HttpClient) {
    super();
  }

  list(query: ExpensesQuery): Observable<ExpensesPage> {
    return this.http
      .post<ExpensesPaginatedResponseDto>(
        `${this.baseUrl}/paginated`,
        toPaginatedRequest(query)
      )
      .pipe(map(toExpensesPage));
  }

  /**
   * La API no tiene endpoint de export, así que se recorren páginas de 200 hasta
   * agotar el total. Se acota con `EXPENSES_EXPORT_MAX_PAGES` para no colgar el
   * navegador si el total creciera mucho; el contenedor avisa si se truncó.
   */
  listAll(query: ExpensesQuery): Observable<Expense[]> {
    const fetchPage = (page: number): Observable<ExpensesPage> =>
      this.list({ ...query, page, pageSize: EXPORT_PAGE_SIZE });

    let page = 1;

    return fetchPage(page).pipe(
      expand((response) => {
        page += 1;
        const fetched = page * EXPORT_PAGE_SIZE - EXPORT_PAGE_SIZE;

        const hasMore =
          fetched < response.totalCount && page <= EXPENSES_EXPORT_MAX_PAGES;

        return hasMore ? fetchPage(page) : [];
      }),
      takeWhile((response) => response.items.length > 0, true),
      reduce<ExpensesPage, Expense[]>(
        (all, response) => [...all, ...response.items],
        []
      )
    );
  }

  getOne(ref: ExpenseRef): Observable<Expense> {
    return this.http
      .get<ExpenseDto>(`${this.baseUrl}/${ref.nodeId}/${ref.expenseId}`)
      .pipe(map(toExpense));
  }

  create(payload: CreateExpensePayload): Observable<Expense> {
    const body = new FormData();

    body.append('nodeId', payload.nodeId);
    body.append('driverId', payload.driverId);
    if (payload.driverName) {
      body.append('driverName', payload.driverName);
    }
    body.append('licensePlate', payload.licensePlate);
    body.append('category', payload.category);
    body.append('description', payload.description);
    body.append('amount', centsToApiAmount(payload.amountInCents));
    body.append('status', String(payload.status));
    this.appendReason(body, payload.status, payload.reason, payload.reasonCode);
    this.appendReceipt(body, payload.receiptFile);

    return this.http.post<ExpenseDto>(this.baseUrl, body).pipe(map(toExpense));
  }

  update(
    ref: ExpenseRef,
    payload: UpdateExpensePayload
  ): Observable<Expense> {
    const body = new FormData();

    body.append('category', payload.category);
    body.append('description', payload.description);
    body.append('amount', centsToApiAmount(payload.amountInCents));
    body.append('status', String(payload.status));
    this.appendReason(body, payload.status, payload.reason, payload.reasonCode);
    this.appendReceipt(body, payload.receiptFile);

    /*
     * Sólo se manda al quitar la foto. Un multipart sin fichero no distingue «no
     * la he tocado» de «la he quitado», y lo primero es lo habitual, así que el
     * comportamiento por defecto es conservarla.
     */
    if (payload.removeReceipt) {
      body.append('removeReceipt', 'true');
    }

    return this.http
      .put<ExpenseDto>(`${this.baseUrl}/${ref.nodeId}/${ref.expenseId}`, body)
      .pipe(map(toExpense));
  }

  remove(ref: ExpenseRef): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}/${ref.nodeId}/${ref.expenseId}`
    );
  }

  changeStatus(
    ref: ExpenseRef,
    payload: ExpenseStatusChangePayload
  ): Observable<void> {
    /*
     * Cada clave se manda sólo si aplica. El contrato responde `InvalidReasonCode`
     * a un código en un estado que no lo admite, así que enviarlo «por si acaso»
     * convertiría una aprobación en un 400.
     */
    const body: ExpenseStatusChangeRequestDto = {
      Status: payload.status,
      ...(payload.reason ? { Reason: payload.reason } : {}),
      ...(takesReturnReason(payload.status) && payload.reasonCode
        ? { ReasonCode: payload.reasonCode }
        : {}),
    };

    return this.http.patch<void>(
      `${this.baseUrl}/${ref.nodeId}/${ref.expenseId}/status`,
      body
    );
  }

  /** El buscador de conductores vive en el proxy de Logistics, no en Tracking. */
  searchDrivers(searchTerm?: string): Observable<ExpenseDriverOption[]> {
    const term = searchTerm?.trim();
    const url = term
      ? `${environment.url}/drivers?nameFilterQuery=${encodeURIComponent(term)}`
      : `${environment.url}/drivers`;

    return this.http
      .get<ExpenseDriverOptionsResponseDto>(url)
      .pipe(map((response) => (response.drivers ?? []).map(toDriverOption)));
  }

  private appendReason(
    body: FormData,
    status: CreateExpensePayload['status'],
    reason: string | null,
    reasonCode: CreateExpensePayload['reasonCode']
  ): void {
    // El backend responde 400 con `ReasonRequired` si falta al rechazar.
    if (keepsFreeText(status) && reason) {
      body.append('reason', reason);
    }

    // Y con `InvalidReasonCode` si llega en un estado que no lo admite.
    if (takesReturnReason(status) && reasonCode) {
      body.append('reasonCode', reasonCode);
    }
  }

  private appendReceipt(body: FormData, file: File | null): void {
    if (file) {
      body.append('receipt', file, file.name);
    }
  }
}
